import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "row" }
const _hoisted_2 = {
  id: "statistic-left-panel",
  class: "col-4"
}
const _hoisted_3 = { class: "display-4 text-center" }
const _hoisted_4 = { class: "table table-borderless table-striped table-sm table-hover" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "text-right text-nowrap" }
const _hoisted_7 = { class: "col-8 overflow-auto" }
const _hoisted_8 = { class: "display-4" }

import { format_price } from '@/helpers/filters'
import * as moment from 'moment'
import { computed, onBeforeMount, reactive, watch } from 'vue'
import { Moment } from 'moment/moment'
import {
  ByCategoryGroupedStatisticItem, ByLagerGroupedStatisticItem,
  ByMonthGroupedStatisticItem,
  ChequeLineItem,
  DateTimeBounds,
  Uuid
} from '@/types/api'
import { rgbToHex } from '@/helpers/tools'
import { ActiveElement, ChartData, ChartEvent, ChartOptions } from 'chart.js'
import { silpoStatisticsApi } from '@/di/api'
import AppBarChart from "@/components/app/chart/AppBarChart.vue";
import AppLoadingMessage from "@/components/app/AppLoadingMessage.vue";
import AppPieChart from "@/components/app/chart/AppPieChart.vue";
import SilpoChequeListItem from "@/components/shopping/silpo/SilpoChequeListItem.vue";

interface Lock {
  byMonthGroupedStatisticQuerying: boolean,
  byCategoryGroupedStatisticQuerying: boolean,
  chequeListQuerying: boolean,
}

interface Data {
  byMonthGroupedPeriod: { from: Moment | null, till: Moment | null },
  byCategoryGroupedPeriod: { from: Moment | null, till: Moment | null },
  byMonthGroupedStatistic: ByMonthGroupedStatisticItem[],
  byCategoryGroupedStatistic: ByCategoryGroupedStatisticItem[],
  chequeLines: ByLagerGroupedStatisticItem[],
  selectedCategoryId: Uuid | null,
  selectedByMonthGroupedChartIndex: number | null,
  selectedByCategoryGroupedChartIndex: number | null,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SilpoStatistic',
  setup(__props) {

const lock = reactive<Lock>({
  byMonthGroupedStatisticQuerying: false,
  byCategoryGroupedStatisticQuerying: false,
  chequeListQuerying: false
})

const data = reactive<Data>({
  byMonthGroupedPeriod: { from: moment().endOf('month').subtract(1, 'years'), till: moment().endOf('month') },
  byCategoryGroupedPeriod: { from: null, till: null },
  byCategoryGroupedStatistic: [],
  byMonthGroupedStatistic: [],
  chequeLines: [],
  selectedCategoryId: null,
  selectedByMonthGroupedChartIndex: null,
  selectedByCategoryGroupedChartIndex: null
})

const byMonthGroupedChart = computed<{ data: ChartData, options: ChartOptions }>(() => {
  return {
    data: ((): ChartData => {
      const length = data.byMonthGroupedStatistic.length

      return {
        datasets: [
          {
            label: 'Total spent',
            backgroundColor: [...Array(length).keys()].map((index) => generateColorForChartPart(length, index, data.selectedByMonthGroupedChartIndex as number)),
            data: data.byMonthGroupedStatistic.map(({ date_time, total_price }) => ({
              x: moment(date_time).toDate(),
              y: total_price
            })) as any // eslint-disable-line @typescript-eslint/no-explicit-any
          }
        ]
      }
    })(),
    options: ((): ChartOptions => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        }
      },
      scales: {
        x: {
          type: 'category',
          labels: data.byMonthGroupedStatistic.map(({ date_time }) => moment(date_time).format('MMMM'))
        },
        y: {
          ticks: {
            // beginAtZero: true
          }
        }
      },
      onClick: async (_: ChartEvent, chartElements: ActiveElement[]): Promise<void> => {
        if (chartElements.length === 0) return

        data.selectedByMonthGroupedChartIndex = chartElements[0].index
        const dateTime = moment(data.byMonthGroupedStatistic[data.selectedByMonthGroupedChartIndex].date_time)

        data.byCategoryGroupedPeriod = {
          from: dateTime.clone().startOf('month'),
          till: dateTime.clone().endOf('month')
        }

        // region Query by category grouped statistic
        data.selectedCategoryId = null
        lock.byCategoryGroupedStatisticQuerying = true

        data.chequeLines = []

        const statisticParams = {
          from: data.byCategoryGroupedPeriod.from?.format(),
          till: data.byCategoryGroupedPeriod.till?.format()
        }

        try {
          const list = await silpoStatisticsApi.getByCategoryGroupedStatistic(statisticParams as DateTimeBounds)

          data.byCategoryGroupedStatistic = list.items
          data.selectedByCategoryGroupedChartIndex = null
        } finally {
          lock.byCategoryGroupedStatisticQuerying = false
        }
        // endregion
      }
    }))()
  }
})

const byCategoryGroupedChart = computed<{ data: ChartData, options: ChartOptions }>(() => ({
  data: ((): ChartData => {
    const length = data.byCategoryGroupedStatistic.length

    return {
      labels: data.byCategoryGroupedStatistic.map(({ category_name }) => category_name),
      datasets: [
        {
          label: 'Data One',
          backgroundColor: [...Array(length).keys()].map((index) => generateColorForChartPart(length, index, data.selectedByCategoryGroupedChartIndex as number)),
          data: data.byCategoryGroupedStatistic.map(({ value }) => value) as any // eslint-disable-line @typescript-eslint/no-explicit-any
        }
      ]
    }
  })(),
  options: ((): ChartOptions => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    onClick: (_: ChartEvent, chartElements: ActiveElement[]): void => {
      if (chartElements.length === 0) return

      setSelectedCategoryIdByChartIndex(chartElements[0].index)
    }
  }))()
}))

const totalSpent = computed<number>((): number => Math.round(data.byCategoryGroupedStatistic.reduce((carry: number, { value }) => carry + value, 0)))

const totalSpentInCategory = computed<number>((): number => Math.round(data.byCategoryGroupedStatistic[data.selectedByCategoryGroupedChartIndex as number].value))

watch(() => data.selectedCategoryId, async (selectedCategoryId): Promise<void> => {
  data.chequeLines = []

  if (!selectedCategoryId) return

  lock.chequeListQuerying = true

  try {
    const chequeLines = await silpoStatisticsApi.getByLagerGroupedStatistic(
        {
          from: data.byCategoryGroupedPeriod.from?.format() as string,
          till: data.byCategoryGroupedPeriod.till?.format() as string
        },
        data.selectedCategoryId as Uuid
    )

    data.chequeLines = chequeLines.items
  } finally {
    lock.chequeListQuerying = false
  }
})

function setSelectedCategoryIdByChartIndex (index: number): void {
  data.selectedCategoryId = data.byCategoryGroupedStatistic[index].category_id
  data.selectedByCategoryGroupedChartIndex = index
}

function generateColorForChartPart (total: number, index: number, selectedIndex: number): string {
  if (index === selectedIndex) return '#01046e'

  return rgbToHex(0, Math.trunc(index * 255 / total), 250)
}

onBeforeMount(async (): Promise<void> => {
  lock.byMonthGroupedStatisticQuerying = true

  try {
    const list = await silpoStatisticsApi.getByMonthGroupedStatistic({
      from: data.byMonthGroupedPeriod.from?.format() as string,
      till: data.byMonthGroupedPeriod.till?.format() as string
    })

    data.byMonthGroupedStatistic = list.items
  } finally {
    lock.byMonthGroupedStatisticQuerying = false
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (data.byMonthGroupedStatistic.length > 0)
      ? (_openBlock(), _createBlock(AppBarChart, {
          key: 0,
          "chart-data": _unref(byMonthGroupedChart).data,
          options: _unref(byMonthGroupedChart).options,
          style: { height: '250px' }
        }, null, 8, ["chart-data", "options"]))
      : (_openBlock(), _createBlock(AppLoadingMessage, { key: 1 })),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        (lock.byCategoryGroupedStatisticQuerying)
          ? (_openBlock(), _createBlock(AppLoadingMessage, { key: 0 }))
          : (data.byCategoryGroupedStatistic.length > 0)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createVNode(AppPieChart, {
                  ref: "chequeChart",
                  "chart-data": _unref(byCategoryGroupedChart).data,
                  options: _unref(byCategoryGroupedChart).options
                }, null, 8, ["chart-data", "options"]),
                _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(totalSpent)) + " грн.", 1),
                _createElementVNode("table", _hoisted_4, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.byCategoryGroupedStatistic, (row, index) => {
                    return (_openBlock(), _createElementBlock("tr", {
                      key: `category-id-${row.category_id}`
                    }, [
                      _createElementVNode("td", null, [
                        _createElementVNode("div", {
                          style: _normalizeStyle([{"height":"24px","width":"12px"}, {'background-color': generateColorForChartPart(index)}]),
                          class: "cursor-pointer",
                          onClick: ($event: any) => (setSelectedCategoryIdByChartIndex(index))
                        }, null, 12, _hoisted_5)
                      ]),
                      _createElementVNode("td", null, _toDisplayString(row.category_name), 1),
                      _createElementVNode("td", _hoisted_6, _toDisplayString(_unref(format_price)(row.value)) + " грн.", 1)
                    ]))
                  }), 128))
                ])
              ], 64))
            : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_7, [
        (lock.chequeListQuerying)
          ? (_openBlock(), _createBlock(AppLoadingMessage, { key: 0 }))
          : (data.chequeLines.length > 0)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createElementVNode("div", _hoisted_8, _toDisplayString(_unref(totalSpentInCategory)) + " грн.", 1),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.chequeLines, (item, index) => {
                  return (_openBlock(), _createBlock(SilpoChequeListItem, {
                    key: `statistic-cheque-line-items-${index}`,
                    "vendor-code": item.lager_id,
                    name: item.name,
                    "image-url": item.image_url,
                    price: item.price,
                    quantity: item.quantity,
                    "category-id": item.category_id,
                    "total-price": item.total_price
                  }, null, 8, ["vendor-code", "name", "image-url", "price", "quantity", "category-id", "total-price"]))
                }), 128))
              ], 64))
            : _createCommentVNode("", true)
      ])
    ])
  ], 64))
}
}

})